// The show's buy bar (push-show.php) slides up once the facts panel,
// .show-header-info, has scrolled out of view above, and back down when it
// returns. It also steps aside while Showtimes & Tickets (its heading and
// forms) is on screen, as it would only repeat that section and cover its
// rows: once the section's top is a quarter of the way up the view, until its
// end is in the top quarter. The view is the window, or the pop-up's
// scroller. The same markup runs on the show page and in the Shortlist pop-up.
//
// It follows scroll position rather than an IntersectionObserver: the panel
// can start below the first screen (a tall photo), and a jump past it (an
// anchor link, or a restored scroll) never crosses an observer threshold.

const watched = new Map<HTMLElement, () => void>();

// The nearest scrolling ancestor in the flat tree, through the Shortlist
// widget's slot and shadow root; null means the window.
function scroller(node: Element): HTMLElement | null {
  let current: Element | null = node;
  while (current) {
    const next: Element | null =
      current.assignedSlot ?? current.parentElement ?? ((current.getRootNode() as ShadowRoot).host || null);
    if (!next || next === document.documentElement || next === document.body) return null;
    const overflow = getComputedStyle(next).overflowY;
    if (overflow === "auto" || overflow === "scroll") return next as HTMLElement;
    current = next;
  }
  return null;
}

function bindBuybar(bar: HTMLElement): void {
  if (watched.has(bar)) return;
  const article = bar.closest("article.show");
  const info = article?.querySelector(".show-header-info");
  if (!article || !info) return;
  // The heading's class, not its id: the Shortlist pop-up prefixes ids.
  const tickets = article.querySelector<HTMLElement>("h3.tickets");
  const forms = article.querySelectorAll<HTMLElement>(".tickets-form-content");
  const ticketsEnd = forms.length ? forms[forms.length - 1] : tickets;

  const scroll = scroller(bar);
  const target: HTMLElement | Window = scroll ?? window;
  let frame = 0;

  const update = (): void => {
    frame = 0;
    const view = scroll?.getBoundingClientRect();
    const top = view ? view.top : 0;
    const bottom = view ? view.bottom : window.innerHeight;
    const quarter = (bottom - top) / 4;
    const onTickets =
      !!tickets &&
      !!ticketsEnd &&
      tickets.getBoundingClientRect().top < bottom - quarter &&
      ticketsEnd.getBoundingClientRect().bottom > top + quarter;
    bar.classList.toggle("is-visible", info.getBoundingClientRect().bottom < top && !onTickets);
  };
  const schedule = (): void => {
    if (!frame) frame = requestAnimationFrame(update);
  };

  target.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", schedule, { passive: true });
  update();
  watched.set(bar, () => {
    cancelAnimationFrame(frame);
    target.removeEventListener("scroll", schedule);
    window.removeEventListener("resize", schedule);
  });
}

export function initShowBuybar(root: ParentNode = document) {
  if (root instanceof HTMLElement && root.matches(".show-buybar")) bindBuybar(root);
  root.querySelectorAll<HTMLElement>(".show-buybar").forEach(bindBuybar);
}

// Pop-up shows come and go: bind bars as they arrive (the widget slots the
// show in after the dialog is open, so its scroller exists), release them
// as they leave.
function watchForInserted() {
  new MutationObserver((records) => {
    let removed = false;
    records.forEach((record) => {
      if (record.removedNodes.length) removed = true;
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) initShowBuybar(node);
      });
    });
    if (!removed) return;
    watched.forEach((release, bar) => {
      if (bar.isConnected) return;
      release();
      watched.delete(bar);
    });
  }).observe(document.body, { childList: true, subtree: true });
}

initShowBuybar();
if (document.body) watchForInserted();
else document.addEventListener("DOMContentLoaded", watchForInserted);
